import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { localizedPath, stripLocalePrefix, useLanguageStore, useTranslation, type Locale } from '@/services/i18n'

const LOCALE_LABELS: Record<Locale, string> = {
  en: 'EN',
  'zh-Hans': '简',
  'zh-Hant': '繁',
}

const LOCALE_OPTIONS: { value: Locale; label: string }[] = [
  { value: 'en', label: 'English' },
  { value: 'zh-Hans', label: '简体中文' },
  { value: 'zh-Hant', label: '繁體中文' },
]

/**
 * Self-contained trigger + dropdown, like SearchBar's own panel — doesn't
 * need to share open/close state with HomePage's hamburger menu, it just
 * sits inside that same top-right button row (see HomePage.tsx).
 */
export function LanguageSwitcher({
  tone = 'dark',
  open: controlledOpen,
  onOpenChange,
}: {
  tone?: 'dark' | 'light'
  open?: boolean
  onOpenChange?: (open: boolean) => void
}) {
  const { t } = useTranslation()
  const locale = useLanguageStore((s) => s.locale)
  const setLocale = useLanguageStore((s) => s.setLocale)
  const location = useLocation()
  const navigate = useNavigate()
  const [internalOpen, setInternalOpen] = useState(false)
  const open = controlledOpen ?? internalOpen
  function changeOpen(nextOpen: boolean) {
    if (controlledOpen === undefined) setInternalOpen(nextOpen)
    onOpenChange?.(nextOpen)
  }

  useEffect(() => {
    if (!open) return
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') changeOpen(false)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open])

  return (
    <div className="header-menu pointer-events-auto relative">
      <button
        onClick={() => changeOpen(!open)}
        aria-label={`${LOCALE_LABELS[locale]} — ${t('home.languageAria')}`}
        aria-expanded={open}
        className={`pointer-events-auto flex h-11 min-w-11 cursor-pointer items-center justify-center rounded-full px-2 font-display text-sm backdrop-blur-md transition ${tone === 'light' ? 'border border-indigo-700 bg-white text-indigo-800 hover:bg-indigo-50' : 'bg-white/10 text-white hover:bg-white/20'}`}
      >
        {LOCALE_LABELS[locale]}
      </button>

      {open && (
          <>
            <div role="presentation" aria-hidden="true" className="fixed inset-0 z-30" onClick={() => changeOpen(false)} />
            <div className="ui-enter-top absolute right-0 top-full z-40 mt-2 flex min-w-32 flex-col gap-1 rounded-2xl bg-slate-900/95 p-2 shadow-2xl backdrop-blur-xl">
              {LOCALE_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  onClick={() => {
                    setLocale(option.value)
                    navigate(localizedPath(option.value, `${location.pathname}${location.search}${location.hash}`))
                    changeOpen(false)
                  }}
                  className={`cursor-pointer whitespace-nowrap rounded-xl px-3 py-2 text-left font-display text-sm transition ${
                    option.value === locale ? 'bg-violet-500 text-white' : 'text-slate-200 hover:bg-white/10'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </>
      )}
    </div>
  )
}

/**
 * Mounted once globally (see App.tsx), same reasoning as BackToGlobeButton —
 * every current/future route gets a way to switch language without each
 * page needing to remember to add its own. Hidden on '/' and '/explore/:id'
 * since those two pages already embed their own better-integrated
 * `<LanguageSwitcher />` directly in their header row (immediately left of
 * `ExplorerHud`'s level pill, and — on HomePage specifically — folding into
 * the hamburger-menu collapse on narrow/short screens like the other action
 * buttons). Keeping this one mounted there too would just render a second,
 * redundant switcher on top of the first.
 */
export function GlobalLanguageSwitcher() {
  const location = useLocation()
  const agnosticPath = stripLocalePrefix(location.pathname)
  if (agnosticPath === '/' || agnosticPath === '/teachers/' || agnosticPath.startsWith('/explore/') || agnosticPath.startsWith('/teachers/lessons/') || agnosticPath.startsWith('/print/') || agnosticPath.startsWith('/present/')) return null

  return (
    <div className="no-print pointer-events-none fixed z-40 top-[max(1rem,env(safe-area-inset-top))] right-[max(1rem,env(safe-area-inset-right))]">
      <LanguageSwitcher />
    </div>
  )
}
